import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Spinner, Text, useMediaQuery, VStack } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { isIOS } from "react-device-detect";

import "../../App.css";
import { FullScreenContext } from "../../contexts/FullScreenContext";
import { useDeviceInfo } from "../../contexts/DeviceContext";
import { LoginPromptModal } from "../../components/modals/LoginPromptModal";
import { AlloprofLogo, FloSplash } from "../../components/APAssetAtlas";
import { IExerciserSDK } from "ap-exerciser-sdk";
import { useVocabIntl } from "../../hooks/useVocabTranslation";
import APButton from "../../components/APButton";

interface SplashProps {
  sdk?: IExerciserSDK;
}

interface LocationState {
  instantLogin: boolean,
  instantRegister: boolean
}

export default function SplashScreen({ sdk }: SplashProps) {
  const [splash, setSplash] = useState(false);
  const { getFormattedString, APFormattedMessage } = useVocabIntl()

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const locationState: React.MutableRefObject<LocationState | null> = useRef(location.state as LocationState);
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");
  const { inBrowser, narrow, landscape } = useDeviceInfo();
  const { toggleFullScreen } = useContext(FullScreenContext);

  const { onLogin, onSignUp, onContinue } = useMemo(() => {
    const onLogin = () => {
      setSplash(false);
      locationState.current = null;
      if (sdk) {
        const observerAppInstance = sdk.getObserverAppInstance()

        if (!sdk.getFirebase().isLoggedIn()) {
          observerAppInstance.trigger("userlogin:show");
        }

        const refreshFlow = async (param: any) => {
          if (sdk.getFirebase().isLoggedIn()) {
            navigate("/game");
          } else {
            setSplash(true);
          }
        }

        observerAppInstance
          .attach("userlogin:done", refreshFlow);
        observerAppInstance
          .attach("userscreen:modal:close", refreshFlow);
        observerAppInstance
          .attach("userscreen:transition:login_to_signup", async () => {
            onSignUp()
          })
        observerAppInstance.attach("userscreen:transition:login_to_forgotpassword", async () => onForgotPassword());
      }
    }

    const onSignUp = () => {
      setSplash(false);
      locationState.current = null;
      if (sdk) {
        const observerAppInstance = sdk.getObserverAppInstance()

        if (!sdk.getFirebase().isLoggedIn()) {
          observerAppInstance.trigger("usercreate:show");
        }

        observerAppInstance
          .attach("usercreate:done", async (shouldUpdate: any) => {
            if (sdk.getFirebase().isLoggedIn()) {
              navigate("/game");
            } else {
              setSplash(true);
            }
          });
        observerAppInstance.attach("userscreen:transition:signup_to_login", async () => onLogin());
      }
    }

    const onContinue = () => {
      navigate("/game?demo=1", { replace: true });
    }

    const onForgotPassword = () => {
      setSplash(false);
      locationState.current = null;
      if (sdk) {
        if (!sdk.getFirebase().isLoggedIn()) {
          sdk.getObserverAppInstance().trigger("userforgotpassword:show");
        }

        const observerAppInstance = sdk.getObserverAppInstance()
        observerAppInstance.attach("userscreen:transition:forgotpassword_to_signup", async () => onSignUp());
        observerAppInstance.attach("userscreen:modal:close", async () => setSplash(true));
      }
    }

    return { onLogin, onSignUp, onContinue };
  }, [navigate, sdk, setSplash])

  useEffect(() => {
    if (sdk && sdk.getFirebase().isLoggedIn()) setIsLoggedIn(true);
    if (locationState.current?.instantLogin) onLogin();
    if (locationState.current?.instantRegister) onSignUp();
  }, [sdk, locationState, onLogin, onSignUp, navigate]);

  const onBegin = () => {
    const appUrl = process.env.REACT_APP_URL
    const isInIframe = () => window.self !== window.top
    try {
      isIOS && isInIframe() ?
        window.open(appUrl, '_blank', 'noreferrer') :
        !isLargeScreen && inBrowser && toggleFullScreen()
    } catch (err) {}
    isLoggedIn ? navigate("/game", { replace: true }) : setSplash(true)
  }

  return (
    <Flex bgImage={"var(--ap-theme-default)"} bgPos={"bottom"} bgSize={"cover"} w={"full"} h={"full"}>
      <VStack className="main-content full-screen" alignItems={"center"} p={"0 1rem"} zIndex={10} w={"full"} h={"full"}
              overflow={"clip"}>
        <AlloprofLogo preserveAspectRatio={"meet"} title="Alloprof" height={"3em"} width={"auto"}
                      margin={["1.5em", null, "2em"]} />
        <Flex gap={"1rem"} alignItems="center" direction={"column"} maxWidth={"max(var(--chakra-sizes-sm),50%)"}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}>
            <Text fontSize="1.5em">Exercice interactif de</Text>
            <Text fontSize="3em" lineHeight="1em" fontWeight={900}>vocabulaire</Text>
          </Box>
          {
            sdk ?
              <>
                <Text fontSize="1em" textAlign="center">
                  <APFormattedMessage id={"splash-motto"} values={{ nl: "" }} />
                </Text>
                <APButton onClick={() => onBegin()}>
                  <APFormattedMessage id={"start-button"} />
                </APButton>
              </> :
              <VStack padding={2} gap={"0.5rem"}>
                <Text fontSize="lg" textAlign="center">
                  <APFormattedMessage id={"splash-getting-ready"} />
                </Text>
                <Text fontSize="md" textAlign="center">
                  <APFormattedMessage id={"splash-getting-ready-wait"} />
                </Text>
                <Spinner size={"lg"} emptyColor={"secondary.fg"} color={"primary.fg"} />
              </VStack>
          }
        </Flex>
        <Box height={{ base: "10em", md: "14em" }} />
        <FloSplash preserveAspectRatio={"meet"} position={"absolute"} zIndex={-1}
                   width={"96%"} height={"auto"} maxWidth={"30rem"} bottom={0} transform={"translate(0,35%)"} />
        <LoginPromptModal isOpen={splash} onSignIn={onLogin} onSignUp={onSignUp} onContinue={onContinue} />
      </VStack>
    </Flex>
  )
}
